/**
 * Vitis Collection Filters - AJAX Filtering
 * Handles dynamic filtering for collection pages
 */

console.log('[Vitis Filters] filters.js loaded');

class CollectionFilters {
  constructor() {
    console.log('[Vitis Filters] CollectionFilters constructor called');
    
    this.form = document.getElementById('FacetFiltersForm');
    this.productsContainer = document.querySelector('.collection__results');
    this.sidebarContainer = document.querySelector('.collection__facets');
    this.productCount = document.querySelector('[data-product-count]');
    this.activeFilters = document.querySelector('.active-facets');
    
    console.log('[Vitis Filters] Form found:', !!this.form);
    console.log('[Vitis Filters] Products container found:', !!this.productsContainer);
    
    // Debounce timer for price inputs
    this.debounceTimer = null;
    this.debounceDelay = 500;
    
    // Loading state
    this.isLoading = false;
    
    if (this.form) {
      this.init();
      console.log('[Vitis Filters] Initialized successfully');
    } else {
      console.warn('[Vitis Filters] Form #FacetFiltersForm not found!');
    }
  }

  init() {
    this.bindEvents();
    // Convert price inputs from cents to euros on initial page load
    this.convertPriceInputsToEuros();
    // NOTE: Don't call handlePopState() here - it causes infinite reload loop
    // handlePopState is only for browser back/forward navigation
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Prevent form submission (causes page reload)
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.onFilterChange(e);
    });

    // Checkbox change events
    this.form.querySelectorAll('.filter-checkbox').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        e.preventDefault();
        this.onFilterChange(e);
      });
    });

    // Price input events with debounce
    this.form.querySelectorAll('.price-input').forEach(input => {
      input.addEventListener('input', (e) => this.onPriceInput(e));
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.onFilterChange(e);
        }
      });
      // Also handle blur for price inputs
      input.addEventListener('blur', (e) => {
        clearTimeout(this.debounceTimer);
        this.onFilterChange(e);
      });
    });

    // Handle browser back/forward
    window.addEventListener('popstate', () => this.handlePopState());
  }

  /**
   * Handle checkbox filter changes
   */
  onFilterChange(event) {
    console.log('[Vitis Filters] onFilterChange called', event?.target);
    
    // Prevent default form behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Skip if we're in the middle of loading
    if (this.isLoading) {
      console.log('[Vitis Filters] Skipped - already loading');
      return;
    }
    
    const formData = new FormData(this.form);
    const searchParams = this.buildSearchParams(formData);
    
    console.log('[Vitis Filters] Search params:', searchParams.toString());
    
    this.updateFilters(searchParams);
  }

  /**
   * Handle price input with debounce
   */
  onPriceInput(event) {
    clearTimeout(this.debounceTimer);
    
    this.debounceTimer = setTimeout(() => {
      this.onFilterChange(event);
    }, this.debounceDelay);
  }

  /**
   * Build search params from form data
   */
  buildSearchParams(formData) {
    const params = new URLSearchParams();
    const currentParams = new URLSearchParams(window.location.search);
    
    // For search pages: preserve 'q' (search query) and 'type' parameters
    if (window.location.pathname.includes('/search')) {
      if (currentParams.has('q')) {
        params.set('q', currentParams.get('q'));
      }
      if (currentParams.has('type')) {
        params.set('type', currentParams.get('type'));
      }
    }
    
    // Get all form entries
    for (const [key, value] of formData.entries()) {
      if (value !== '' && value !== null) {
        // Handle price values - Shopify uses EUROS in URL, not cents!
        if (key.includes('price') && key.includes('filter')) {
          // Handle Italian locale: replace comma with dot for decimal
          const normalizedValue = String(value).replace(',', '.');
          const numValue = parseFloat(normalizedValue);
          
          console.log('[Vitis Filters] Price input:', value, '-> sending:', numValue, '€');
          
          if (!isNaN(numValue) && numValue > 0) {
            // Pass value directly in euros (Shopify expects euros, not cents!)
            params.append(key, numValue);
          }
        } else {
          params.append(key, value);
        }
      }
    }
    
    // Preserve sort_by from current URL if not in form
    if (currentParams.has('sort_by') && !params.has('sort_by')) {
      params.set('sort_by', currentParams.get('sort_by'));
    }
    
    return params;
  }

  /**
   * Update filters via AJAX
   * @param {URLSearchParams} searchParams - The search parameters
   * @param {boolean} skipPushState - If true, don't add to browser history (for back/forward navigation)
   */
  async updateFilters(searchParams, skipPushState = false) {
    console.log('[Vitis Filters] updateFilters called, skipPushState:', skipPushState);
    
    this.isLoading = true;
    this.showLoadingState();
    
    // Save open details state before update
    const openDetailsState = this.saveDetailsState();
    
    // Build new URL
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    console.log('[Vitis Filters] New URL:', newUrl);
    
    // Update browser history only if not from popstate
    if (!skipPushState) {
      history.pushState({ filters: true }, '', newUrl);
    }
    
    try {
      // Fetch new content with section rendering
      const sectionId = this.getSectionId();
      const fetchUrl = `${newUrl}&section_id=${sectionId}`;
      
      console.log('[Vitis Filters] Fetching:', fetchUrl);
      
      const response = await fetch(fetchUrl);
      
      console.log('[Vitis Filters] Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      console.log('[Vitis Filters] HTML received, length:', html.length);
      
      // Parse and update content
      this.updateContent(html, openDetailsState);
      console.log('[Vitis Filters] Content updated');
      
      // Convert price inputs from cents to euros after DOM update
      this.convertPriceInputsToEuros();
      
      // Scroll to top of products (optional)
      this.scrollToProducts();
      
    } catch (error) {
      console.error('[Vitis Filters] Filter update failed:', error);
      // Don't reload - just log the error and let user retry
    } finally {
      this.isLoading = false;
      this.hideLoadingState();
    }
  }

  /**
   * Get the section ID for section rendering
   */
  getSectionId() {
    // Find the main-collection or main-search section specifically
    // Look for section containing our form or products container
    const formSection = this.form?.closest('[id^="shopify-section-"]');
    if (formSection) {
      return formSection.id.replace('shopify-section-', '');
    }
    
    const productsSection = this.productsContainer?.closest('[id^="shopify-section-"]');
    if (productsSection) {
      return productsSection.id.replace('shopify-section-', '');
    }
    
    // Look for section with main-collection class
    const mainCollectionSection = document.querySelector('[id^="shopify-section-"][class*="main-collection"]');
    if (mainCollectionSection) {
      return mainCollectionSection.id.replace('shopify-section-', '');
    }
    
    // Look for section with main-search class
    const mainSearchSection = document.querySelector('[id^="shopify-section-"][class*="main-search"]');
    if (mainSearchSection) {
      return mainSearchSection.id.replace('shopify-section-', '');
    }
    
    // Fallback based on page type
    if (window.location.pathname.includes('/search')) {
      return 'main-search';
    }
    return 'main-collection';
  }

  /**
   * Save which <details> elements are open
   */
  saveDetailsState() {
    const openDetails = [];
    
    if (this.sidebarContainer) {
      this.sidebarContainer.querySelectorAll('details').forEach((detail, index) => {
        if (detail.open) {
          openDetails.push(index);
        }
      });
    }
    
    // Also check drawer
    const drawer = document.getElementById('facets-drawer');
    if (drawer) {
      drawer.querySelectorAll('details').forEach((detail, index) => {
        if (detail.open && !openDetails.includes(index)) {
          openDetails.push(index);
        }
      });
    }
    
    return openDetails;
  }

  /**
   * Restore <details> open state
   */
  restoreDetailsState(container, openIndices) {
    if (!container) return;
    
    container.querySelectorAll('details').forEach((detail, index) => {
      detail.open = openIndices.includes(index);
    });
  }

  /**
   * Update page content with new HTML
   */
  updateContent(html, openDetailsState) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Update products container
    const newProducts = doc.querySelector('.collection__results');
    if (newProducts && this.productsContainer) {
      this.productsContainer.innerHTML = newProducts.innerHTML;
      
      // Re-initialize any custom elements or components
      this.reinitializeComponents();
    }
    
    // Update sidebar filters (to refresh counts)
    const newSidebar = doc.querySelector('.collection__facets');
    if (newSidebar && this.sidebarContainer) {
      this.sidebarContainer.innerHTML = newSidebar.innerHTML;
      
      // Restore open details state
      this.restoreDetailsState(this.sidebarContainer, openDetailsState);
      
      // Re-bind events for new sidebar content
      this.rebindSidebarEvents();
    }
    
    // Update drawer sidebar too
    const drawer = document.getElementById('facets-drawer');
    const newDrawerContent = doc.querySelector('#facets-drawer .custom-sidebar');
    if (drawer && newDrawerContent) {
      const drawerSidebar = drawer.querySelector('.custom-sidebar');
      if (drawerSidebar) {
        drawerSidebar.innerHTML = newDrawerContent.innerHTML;
        this.restoreDetailsState(drawerSidebar, openDetailsState);
        this.rebindDrawerEvents();
      }
    }
    
    // Update active filters display
    const newActiveFilters = doc.querySelector('.active-facets');
    if (newActiveFilters && this.activeFilters) {
      this.activeFilters.innerHTML = newActiveFilters.innerHTML;
    }
    
    // Update product count
    const newCount = doc.querySelector('[data-product-count]');
    if (newCount && this.productCount) {
      this.productCount.innerHTML = newCount.innerHTML;
    }
    
    // Update page title if needed
    const newTitle = doc.querySelector('title');
    if (newTitle) {
      document.title = newTitle.textContent;
    }

    // Update mobile drawer
    if (window.mobileFilterDrawer) {
      window.mobileFilterDrawer.updateContent(html);
    } else {
      // Update mobile drawer content manually
      const mobileDrawer = document.getElementById('MobileFilterDrawer');
      const newMobileDrawer = doc.getElementById('MobileFilterDrawer');
      if (mobileDrawer && newMobileDrawer) {
        const wasOpen = mobileDrawer.classList.contains('is-open');
        const panel = mobileDrawer.querySelector('.drawer-content');
        const newPanel = newMobileDrawer.querySelector('.drawer-content');
        if (panel && newPanel) {
          panel.innerHTML = newPanel.innerHTML;
        }
        if (wasOpen) {
          mobileDrawer.classList.add('is-open');
        }
      }
    }
    
    // Update mobile toolbar sort select to match current sort
    const currentSortBy = new URLSearchParams(window.location.search).get('sort_by');
    document.querySelectorAll('.js-sort-select').forEach(select => {
      if (currentSortBy) {
        select.value = currentSortBy;
      }
    });
    
  }

  /**
   * Rebind events for sidebar after AJAX update
   */
  rebindSidebarEvents() {
    // Update form reference
    this.form = document.getElementById('FacetFiltersForm');
    
    if (!this.form) return;
    
    // Prevent form submission
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.onFilterChange(e);
    });
    
    // Rebind checkbox events
    this.form.querySelectorAll('.filter-checkbox').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        e.preventDefault();
        this.onFilterChange(e);
      });
    });
    
    // Rebind price input events
    this.form.querySelectorAll('.price-input').forEach(input => {
      input.addEventListener('input', (e) => this.onPriceInput(e));
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.onFilterChange(e);
        }
      });
      input.addEventListener('blur', (e) => {
        clearTimeout(this.debounceTimer);
        this.onFilterChange(e);
      });
    });
  }

  /**
   * Rebind events for drawer after AJAX update
   */
  rebindDrawerEvents() {
    const drawer = document.getElementById('facets-drawer');
    if (!drawer) return;
    
    const drawerForm = drawer.querySelector('#FacetFiltersForm, .custom-sidebar form, .custom-sidebar');
    if (!drawerForm) return;
    
    drawerForm.querySelectorAll('.filter-checkbox').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => this.onFilterChange(e));
    });
    
    drawerForm.querySelectorAll('.price-input').forEach(input => {
      input.addEventListener('input', (e) => this.onPriceInput(e));
    });
  }

  /**
   * Re-initialize custom components after content update
   */
  reinitializeComponents() {
    // Trigger reveal animations if using reveal-items
    const revealItems = this.productsContainer.querySelector('reveal-items');
    if (revealItems && typeof revealItems.connectedCallback === 'function') {
      revealItems.connectedCallback();
    }
    
    // Re-initialize product cards if needed
    document.querySelectorAll('product-card').forEach(card => {
      if (typeof card.connectedCallback === 'function') {
        card.connectedCallback();
      }
    });
    
    // Re-initialize sort selects after AJAX update
    if (typeof initSortSelects === 'function') {
      initSortSelects();
    }
    
    // Dispatch custom event for other scripts to hook into
    document.dispatchEvent(new CustomEvent('filters:updated', {
      detail: { container: this.productsContainer }
    }));
  }

  /**
   * Show loading state
   */
  showLoadingState() {
    // Add loading class to containers
    if (this.productsContainer) {
      this.productsContainer.classList.add('is-loading');
      this.productsContainer.style.opacity = '0.5';
      this.productsContainer.style.pointerEvents = 'none';
    }
    
    // Add loading overlay
    const loader = document.createElement('div');
    loader.id = 'filters-loading';
    loader.innerHTML = `
      <div style="
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 9999;
        background: white;
        padding: 20px 30px;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.15);
        display: flex;
        align-items: center;
        gap: 12px;
      ">
        <svg class="animate-spin" style="width: 24px; height: 24px; animation: spin 1s linear infinite;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle style="opacity: 0.25;" cx="12" cy="12" r="10" stroke="#a3b835" stroke-width="4"></circle>
          <path style="opacity: 0.75;" fill="#a3b835" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span style="color: #1e3a5f; font-weight: 500;">Caricamento...</span>
      </div>
    `;
    document.body.appendChild(loader);
    
    // Add spin animation if not exists
    if (!document.getElementById('spin-animation')) {
      const style = document.createElement('style');
      style.id = 'spin-animation';
      style.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
      document.head.appendChild(style);
    }
  }

  /**
   * Hide loading state
   */
  hideLoadingState() {
    if (this.productsContainer) {
      this.productsContainer.classList.remove('is-loading');
      this.productsContainer.style.opacity = '';
      this.productsContainer.style.pointerEvents = '';
    }
    
    const loader = document.getElementById('filters-loading');
    if (loader) {
      loader.remove();
    }
  }

  /**
   * Scroll to products container
   */
  /**
   * Sync price inputs with URL values
   * Shopify uses EUROS in filter URLs, not cents!
   */
  convertPriceInputsToEuros() {
    // Get current URL params
    const urlParams = new URLSearchParams(window.location.search);
    
    document.querySelectorAll('.price-input').forEach(input => {
      const paramName = input.name;
      const urlValue = urlParams.get(paramName);
      
      // If there's a value in the URL, use it directly (it's already in euros)
      if (urlValue) {
        const euros = parseFloat(urlValue);
        if (!isNaN(euros) && euros > 0) {
          // Format nicely (remove unnecessary decimals)
          input.value = euros % 1 === 0 ? euros : euros.toFixed(2);
          console.log('[Vitis Filters] URL param', paramName, ':', urlValue, '€ -> input:', input.value);
        }
      }
    });
  }

  scrollToProducts() {
    if (!this.productsContainer) return;
    
    const headerHeight = document.querySelector('header')?.offsetHeight || 0;
    const targetPosition = this.productsContainer.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;
    
    // Only scroll if user is below the products
    if (window.pageYOffset > targetPosition) {
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Handle browser back/forward navigation
   */
  handlePopState() {
    console.log('[Vitis Filters] handlePopState called, URL:', window.location.href);
    
    // Re-fetch content for the current URL
    const searchParams = new URLSearchParams(window.location.search);
    
    // Use AJAX but skip pushState (we're navigating history, not creating new entry)
    this.updateFilters(searchParams, true);
  }
}

/**
 * Mobile Filter Drawer Controller
 */
class MobileFilterDrawer {
  constructor(collectionFilters) {
    this.collectionFilters = collectionFilters;
    this.drawer = document.getElementById('MobileFilterDrawer');
    this.openBtn = document.getElementById('MobileFilterBtn');
    this.mobileForm = document.getElementById('MobileFiltersForm');
    
    if (this.drawer) {
      this.init();
    }
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Open button
    if (this.openBtn) {
      this.openBtn.addEventListener('click', () => this.open());
    }

    // Close buttons (backdrop and X button)
    this.drawer.querySelectorAll('[data-drawer-close]').forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    // Apply filters button
    const applyBtn = this.drawer.querySelector('[data-apply-filters]');
    if (applyBtn) {
      applyBtn.addEventListener('click', () => this.applyFilters());
    }

    // Prevent scroll on body when drawer is open
    this.drawer.addEventListener('transitionend', () => {
      if (this.drawer.classList.contains('is-open')) {
        document.body.style.overflow = 'hidden';
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });

    // Sync mobile checkboxes with desktop (optional - for visual consistency)
    this.syncFiltersState();
  }

  /**
   * Open the drawer
   */
  open() {
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    
    // Sync filter state from URL
    this.syncFiltersState();
    
    // Focus first interactive element for accessibility
    setTimeout(() => {
      const firstCheckbox = this.drawer.querySelector('.drawer-checkbox, .drawer-price-input');
      if (firstCheckbox) {
        firstCheckbox.focus();
      }
    }, 300);
  }

  /**
   * Close the drawer
   */
  close() {
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    
    // Return focus to open button
    if (this.openBtn) {
      this.openBtn.focus();
    }
  }

  /**
   * Sync mobile filters with current URL state
   */
  syncFiltersState() {
    const currentParams = new URLSearchParams(window.location.search);
    
    // Sync checkboxes
    this.drawer.querySelectorAll('.mobile-filter-checkbox').forEach(checkbox => {
      const values = currentParams.getAll(checkbox.name);
      checkbox.checked = values.includes(checkbox.value);
    });

    // Sync price inputs
    this.drawer.querySelectorAll('.mobile-price-input').forEach(input => {
      const value = currentParams.get(input.name);
      if (value) {
        // Convert from cents to euros
        input.value = Math.round(parseFloat(value) / 100);
      } else {
        input.value = '';
      }
    });
  }

  /**
   * Apply filters from mobile form
   */
  applyFilters() {
    if (!this.mobileForm || !this.collectionFilters) {
      // Fallback: submit form traditionally
      if (this.mobileForm) {
        const formData = new FormData(this.mobileForm);
        const params = new URLSearchParams();
        
        for (const [key, value] of formData.entries()) {
          if (value !== '' && value !== null) {
            if (key.includes('price') && key.includes('filter')) {
              const numValue = parseFloat(value);
              if (!isNaN(numValue)) {
                params.append(key, Math.round(numValue * 100));
              }
            } else {
              params.append(key, value);
            }
          }
        }
        
        window.location.href = `${window.location.pathname}?${params.toString()}`;
      }
      return;
    }

    // Use AJAX filtering
    const formData = new FormData(this.mobileForm);
    const searchParams = this.collectionFilters.buildSearchParams(formData);
    
    // Close drawer first
    this.close();
    
    // Apply filters via AJAX
    this.collectionFilters.updateFilters(searchParams);
  }

  /**
   * Update drawer content after AJAX refresh
   */
  updateContent(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const newDrawer = doc.getElementById('MobileFilterDrawer');
    if (newDrawer && this.drawer) {
      // Save open state
      const wasOpen = this.drawer.classList.contains('is-open');
      const openDetails = [];
      
      this.drawer.querySelectorAll('details').forEach((detail, index) => {
        if (detail.open) openDetails.push(index);
      });
      
      // Update content
      const drawerPanel = this.drawer.querySelector('.drawer-panel');
      const newPanel = newDrawer.querySelector('.drawer-panel');
      
      if (drawerPanel && newPanel) {
        drawerPanel.innerHTML = newPanel.innerHTML;
      }
      
      // Restore state
      if (wasOpen) {
        this.drawer.classList.add('is-open');
      }
      
      this.drawer.querySelectorAll('details').forEach((detail, index) => {
        detail.open = openDetails.includes(index);
      });
      
      // Rebind events
      this.bindEvents();
    }
  }
}

/**
 * Sort Select Handler
 * Handles sort dropdown changes while preserving all URL parameters (filters, search query, etc.)
 */
function initSortSelects() {
  document.querySelectorAll('.js-sort-select').forEach(select => {
    select.addEventListener('change', function(e) {
      e.preventDefault();
      
      const sortValue = this.value;
      const currentParams = new URLSearchParams(window.location.search);
      
      // Update or set the sort_by parameter
      if (sortValue) {
        currentParams.set('sort_by', sortValue);
      } else {
        currentParams.delete('sort_by');
      }
      
      // Build new URL preserving all other parameters
      const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
      
      console.log('[Vitis Sort] Sorting by:', sortValue);
      console.log('[Vitis Sort] New URL:', newUrl);
      
      // Navigate to new URL
      window.location.href = newUrl;
    });
  });
  
  console.log('[Vitis Sort] Sort selects initialized:', document.querySelectorAll('.js-sort-select').length);
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('[Vitis Filters] DOMContentLoaded fired');
  
  // Initialize sort selects
  initSortSelects();
  
  // Initialize if form exists (means we're on a collection or search page with filters)
  const form = document.getElementById('FacetFiltersForm');
  const drawer = document.getElementById('MobileFilterDrawer');
  
  console.log('[Vitis Filters] Form found:', !!form);
  console.log('[Vitis Filters] Drawer found:', !!drawer);
  
  if (form) {
    window.collectionFilters = new CollectionFilters();
  }
  
  if (drawer && window.collectionFilters) {
    window.mobileFilterDrawer = new MobileFilterDrawer(window.collectionFilters);
  }
});

// Also initialize on section re-render (for Shopify theme editor)
document.addEventListener('shopify:section:load', (event) => {
  // Re-initialize sort selects
  initSortSelects();
  
  const form = document.getElementById('FacetFiltersForm');
  const drawer = document.getElementById('MobileFilterDrawer');
  
  if (form) {
    window.collectionFilters = new CollectionFilters();
  }
  
  if (drawer && window.collectionFilters) {
    window.mobileFilterDrawer = new MobileFilterDrawer(window.collectionFilters);
  }
});

/**
 * Infinite Scroll for Collection Pages
 */
class InfiniteScroll {
  constructor() {
    this.productList = document.querySelector('.product-list');
    this.pagination = document.querySelector('.collection__pagination');
    this.nextPageLink = this.pagination?.querySelector('a[rel="next"]');
    
    if (!this.productList || !this.nextPageLink) {
      console.log('[Vitis Infinite Scroll] No pagination found, skipping');
      return;
    }
    
    this.isLoading = false;
    this.hasMore = true;
    
    this.init();
  }
  
  init() {
    // Hide pagination
    if (this.pagination) {
      this.pagination.style.display = 'none';
    }
    
    // Create loader element
    this.createLoader();
    
    // Set up intersection observer
    this.setupObserver();
    
    console.log('[Vitis Infinite Scroll] Initialized');
  }
  
  createLoader() {
    // Create trigger element (always visible, used for IntersectionObserver)
    this.trigger = document.createElement('div');
    this.trigger.className = 'infinite-scroll-trigger';
    this.trigger.style.cssText = `
      height: 1px;
      width: 100%;
    `;
    
    // Create loader element (visible only when loading)
    this.loader = document.createElement('div');
    this.loader.className = 'infinite-scroll-loader';
    this.loader.innerHTML = `
      <div class="infinite-scroll-spinner">
        <svg class="animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#a3b835" stroke-width="2">
          <circle cx="12" cy="12" r="10" stroke-opacity="0.25"></circle>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"></path>
        </svg>
      </div>
      <span>Caricamento prodotti...</span>
    `;
    this.loader.style.cssText = `
      display: none;
      justify-content: center;
      align-items: center;
      gap: 12px;
      padding: 32px;
      color: #6b7280;
      font-size: 14px;
    `;
    
    // Insert after product list
    this.productList.parentNode.insertBefore(this.trigger, this.productList.nextSibling);
    this.trigger.parentNode.insertBefore(this.loader, this.trigger.nextSibling);
    
    // Create "end of results" message
    this.endMessage = document.createElement('div');
    this.endMessage.className = 'infinite-scroll-end';
    this.endMessage.innerHTML = '<p>Hai visualizzato tutti i prodotti</p>';
    this.endMessage.style.cssText = `
      display: none;
      text-align: center;
      padding: 32px;
      color: #9ca3af;
      font-size: 14px;
    `;
    this.loader.parentNode.insertBefore(this.endMessage, this.loader.nextSibling);
  }
  
  setupObserver() {
    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0
    };
    
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading && this.hasMore) {
          this.loadMore();
        }
      });
    }, options);
    
    // Observe the trigger element (always visible) instead of the loader
    this.observer.observe(this.trigger);
  }
  
  async loadMore() {
    if (!this.nextPageLink || this.isLoading) return;
    
    this.isLoading = true;
    this.loader.style.display = 'flex';
    
    try {
      const response = await fetch(this.nextPageLink.href);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get new products
      const newProducts = doc.querySelectorAll('.product-list > *');
      
      // Append new products
      newProducts.forEach(product => {
        this.productList.appendChild(product.cloneNode(true));
      });
      
      // Check for next page
      const newPagination = doc.querySelector('.collection__pagination');
      this.nextPageLink = newPagination?.querySelector('a[rel="next"]');
      
      if (!this.nextPageLink) {
        this.hasMore = false;
        this.trigger.style.display = 'none';
        this.loader.style.display = 'none';
        this.endMessage.style.display = 'block';
        this.observer.disconnect();
      }
      
      console.log('[Vitis Infinite Scroll] Loaded more products');
      
    } catch (error) {
      console.error('[Vitis Infinite Scroll] Error loading more:', error);
    } finally {
      this.isLoading = false;
      if (this.hasMore) {
        this.loader.style.display = 'none';
      }
    }
  }
}

/**
 * Scroll to Top Button
 */
class ScrollToTop {
  constructor() {
    this.createButton();
    this.bindEvents();
    console.log('[Vitis ScrollToTop] Initialized');
  }
  
  createButton() {
    this.button = document.createElement('button');
    this.button.className = 'scroll-to-top-btn';
    this.button.setAttribute('aria-label', 'Torna su');
    this.button.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M18 15l-6-6-6 6"/>
      </svg>
    `;
    this.button.style.cssText = `
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 40;
      width: 48px;
      height: 48px;
      background-color: #1e3a5f;
      color: white;
      border: none;
      border-radius: 50%;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      cursor: pointer;
      display: none;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
    `;
    
    document.body.appendChild(this.button);
  }
  
  bindEvents() {
    // Show/hide based on scroll position
    window.addEventListener('scroll', () => {
      if (window.scrollY > 400) {
        this.button.style.display = 'flex';
      } else {
        this.button.style.display = 'none';
      }
    });
    
    // Scroll to top on click
    this.button.addEventListener('click', () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
    
    // Hover effect
    this.button.addEventListener('mouseenter', () => {
      this.button.style.backgroundColor = '#2a4a73';
      this.button.style.transform = 'scale(1.05)';
    });
    
    this.button.addEventListener('mouseleave', () => {
      this.button.style.backgroundColor = '#1e3a5f';
      this.button.style.transform = 'scale(1)';
    });
  }
}

// Initialize Infinite Scroll and Scroll to Top
document.addEventListener('DOMContentLoaded', () => {
  // Only on collection pages
  if (document.querySelector('.product-list')) {
    window.infiniteScroll = new InfiniteScroll();
  }
  
  // Scroll to top on all pages
  window.scrollToTop = new ScrollToTop();
});

